import type {
  TextModeObservation,
  QuestionItem,
  TextModeExtraction,
  TextModePatientInfo,
  TextModeSubmitResponse,
  ResponseOption,
  NumericOptions,
  DateOptions,
  EvidenceItem,
  QuestionStage,
  BloodPressureOptions,
  QuestionValidation,
  ObservationInputType,
  AssessmentScope,
} from './types/text-mode.types';
import { newCorrelation } from '@/logging/correlation';
import { logger } from '@/logging/logger';
import { LogCategory } from '@/logging/types';

export class AssessmentConfigurationError extends Error {
  readonly code = 'ASSESSMENT_CONFIGURATION_ERROR';
}

interface FlexiQuestion {
  name: string;
  type: 'option' | 'number' | 'date' | 'text';
  promptToken: string;
  responses?: ResponseOption[];
  options?: NumericOptions;
  dateOptions?: DateOptions;
  observationCategory?: TextModeObservation['category'];
  input?: ObservationInputType;
  initialAnswer?: string | number;
  validation?: QuestionValidation;
  bloodPressureOptions?: BloodPressureOptions;
}

type FlexiExtraction = TextModeExtraction;

export interface FlexiObservation extends TextModeObservation {
  name: string;
  value: string | number | boolean;
  type: 'option' | 'number' | 'date' | 'text';
  category: 'patient' | 'vital_signs' | 'clinical_observation';
  source?: 'note' | 'response' | 'computed' | 'assumed';
  nameToken?: string;
  responseOptions?: ResponseOption[] | null;
  numericOptions?: NumericOptions | null;
  input?: ObservationInputType;
  bloodPressureOptions?: BloodPressureOptions | null;
  extractionID?: string | null;
}

type FlexiPatientInfo = TextModePatientInfo;

interface FlexiEvaluateResponse {
  questions: FlexiQuestion[];
  conditions?: string[];
  comprehensive: boolean;
  extractions: FlexiExtraction[];
  ambiguousExtractions?: FlexiExtraction[];
  observations: FlexiObservation[];
  patient?: FlexiPatientInfo;
  unmappedExtractions?: FlexiExtraction[];
  focusedAssessments: string[];
  questionStage?: QuestionStage;
}

interface SubmitClinicalNoteOptions {
  engagement?: string;
  observations?: FlexiObservation[];
  patient?: TextModePatientInfo | null;
  processNote: boolean;
  extractions?: TextModeExtraction[];
  assessmentScope?: AssessmentScope;
}

interface FlexiEvaluateRequestBody {
  encounterId: string;
  engagement?: string;
  note: string;
  processNote: boolean;
  patient: TextModePatientInfo;
  observations?: FlexiObservation[];
  extractions?: TextModeExtraction[];
  assessmentScope?: AssessmentScope;
}

function formatGender(sex?: 'male' | 'female' | null): string {
  if (!sex) return ''
  return sex === 'male' ? 'Male' : 'Female'
}

function buildPatientEvidenceItems(patient?: FlexiPatientInfo): EvidenceItem[] {
  const items: EvidenceItem[] = [
    {
      id: 'patient-age',
      name: 'Age',
      nameToken: 'app.assessment_mode.text.input.field.age',
      value: patient?.ageInDays ?? null,
      valueFormatter: 'age' as const,
      editable: true,
      readOnly: true,
    },
    {
      id: 'patient-gender',
      name: 'Gender',
      nameToken: 'app.assessment_mode.text.input.field.gender',
      value: formatGender(patient?.sex),
      editable: true,
      readOnly: true,
    },
  ]

  if (patient?.maternal_status && patient.maternal_status !== 'none') {
    items.push({
      id: 'patient-maternal-status',
      name: 'Maternal Status',
      nameToken: 'questions.maternal_status.title',
      value: patient.maternal_status,
      valueToken: `condition_patient_type.${patient.maternal_status}`,
      editable: true,
      readOnly: true,
    })
  }

  return items
}

function toObservationStatus(
  value: string | number | boolean,
  responseOptions?: ResponseOption[] | null,
): 'positive' | 'negative' | 'unknown' | undefined {
  const normalized = String(value).trim().toLowerCase()
  const compact = normalized.replace(/ /g, '_')

  // Standard yes/no/unknown mappings — unchanged
  if (compact === 'yes' || compact === 'true' || compact === 'present') {
    return 'positive'
  }

  if (compact === 'no' || compact === 'false' || compact === 'absent') {
    return 'negative'
  }

  if (compact === 'unknown') {
    return 'unknown'
  }

  // Option questions with non-standard values:
  // If the question has a 'none' option, that value is negative; everything else is positive.
  // If there is no 'none' option (e.g. uterus hard/soft), always positive.
  // That includes test results: an RDT 'negative' is a reported answer.
  if (responseOptions && responseOptions.length > 0) {
    const hasNoneOption = responseOptions.some(
      (opt) => opt.value.trim().toLowerCase() === 'none',
    )
    if (hasNoneOption) {
      return compact === 'none' ? 'negative' : 'positive'
    }
    return 'positive'
  }

  if (compact === 'positive') {
    return 'positive'
  }

  if (compact === 'negative') {
    return 'negative'
  }

  return undefined
}

function normalizedResponseValue(value: string | number | boolean): string {
  return String(value).trim().toLowerCase()
}

const MEASUREMENTS_WITH_UNKNOWN_STATUS = new Set(['weight', 'height'])

function observationValueToken(
  value: string | number | boolean,
  responseOptions?: ResponseOption[] | null,
): string | undefined {
  const normalizedValue = normalizedResponseValue(value)
  return responseOptions?.find(
    option => normalizedResponseValue(option.value) === normalizedValue,
  )?.token
}

function mapObservationToEvidenceItem(observation: FlexiObservation, index: number): EvidenceItem {
  let mappedStatus: 'positive' | 'negative' | 'unknown' | undefined

  // Vitals are measurements — they don't need positive/negative classification
  const isClinicalObservation = observation.category === 'clinical_observation'

  if (observation.type === 'option' && isClinicalObservation) {
    // Option questions: use value + responseOptions context to determine status
    mappedStatus = toObservationStatus(observation.value, observation.responseOptions)
  } else if (
    isClinicalObservation
    && MEASUREMENTS_WITH_UNKNOWN_STATUS.has(observation.name)
    && normalizedResponseValue(observation.value) === 'unknown'
  ) {
    mappedStatus = 'unknown'
  } else if (
    isClinicalObservation &&
    (observation.type === 'number' ||
    observation.type === 'date' ||
    observation.type === 'text')
  ) {
    // Duration, frequency, clinical text/date observations — always positive
    mappedStatus = 'positive'
  }


  return {
    id: `${observation.name}-${index}`,
    name: observation.name,
    nameToken: observation.nameToken,
    value: observation.value,
    valueToken: observationValueToken(observation.value, observation.responseOptions),
    structured: true,
    observationStatus: mappedStatus,
    source: observation.source,
    editable: true,
    observationName: observation.name,
    observationCategory: observation.category,
    observationType: observation.type,
    responseOptions: observation.responseOptions ?? null,
    numericOptions: observation.numericOptions ?? null,
    input: observation.input,
    bloodPressureOptions: observation.bloodPressureOptions ?? null,
    extractionID: observation.extractionID ?? null,
  }
}

const FLEXI_EVALUATE_ENDPOINT = '/api/v1/flexi/evaluate';

type FlexiQuestionMetadata = Pick<
  FlexiQuestion,
  'input' | 'initialAnswer' | 'validation' | 'bloodPressureOptions'
>;

function mapQuestionMetadata(question: FlexiQuestion): FlexiQuestionMetadata {
  const metadata: FlexiQuestionMetadata = {};

  if (question.input) metadata.input = question.input;
  if (question.initialAnswer !== undefined) metadata.initialAnswer = question.initialAnswer;
  if (question.validation) metadata.validation = question.validation;
  if (question.bloodPressureOptions) {
    metadata.bloodPressureOptions = question.bloodPressureOptions;
  }

  return metadata;
}

export function mapEvaluateResponseToTextMode(response: FlexiEvaluateResponse): TextModeSubmitResponse {
  const vitalObservations = (response.observations || []).filter((observation) => {
    return observation.category === 'vital_signs' && observation.source !== 'computed'
  })

  const clinicalObservations = (response.observations || []).filter((observation) => {
    return observation.category === 'clinical_observation'
  })

  const questions: QuestionItem[] = (response.questions || []).map((question) => {
    const base = {
      id: question.name,
      promptToken: question.promptToken,
      observationCategory: question.observationCategory ?? 'patient',
      answer: question.initialAnswer === undefined ? '' : String(question.initialAnswer),
      expanded: true,
      ...mapQuestionMetadata(question),
    }

    switch (question.type) {
      case 'option':
        return {
          ...base,
          type: 'option' as const,
          responses: question.responses || [],
        }
      case 'number':
        return {
          ...base,
          type: 'number' as const,
          options: question.options || {
            min: 0,
            max: 0,
            unit: '',
            decimalPlaces: 0,
            allowUnknown: true,
          },
        }
      case 'date':
        return {
          ...base,
          type: 'date' as const,
          dateOptions: question.dateOptions,
        }
      default:
        return {
          ...base,
          type: 'text' as const,
        }
    }
  })

  const hasInputObservations = response.observations.some(
    observation => observation.source !== 'computed',
  );

  const isNotClinicalConcept = !response.comprehensive
    && response.questions.length === 0
    && (response.ambiguousExtractions?.length ?? 0) === 0
    && response.extractions.length === 0
    && !hasInputObservations;
  const isNoFocusedLogic = !isNotClinicalConcept
    && !response.comprehensive
    && response.focusedAssessments.length === 0
    && response.questions.length === 0;
  const isComprehensive = response.comprehensive;

  return {
    questions,
    patient: response.patient ?? null,
    ambiguousExtractions: response.ambiguousExtractions || [],
    extractions: response.extractions || [],
    observations: response.observations || [],
    conditions: response.conditions || [],
    evidenceCategories: [
      {
        id: 'patient',
        title: 'Patient',
        items: buildPatientEvidenceItems(response.patient),
      },
      {
        id: 'vitals',
        title: 'Vitals',
        items: vitalObservations.map((observation, index) => mapObservationToEvidenceItem(observation, index)),
      },
      {
        id: 'clinical-observations',
        title: 'Clinical Observations',
        items: clinicalObservations
          .map((observation, index) => mapObservationToEvidenceItem(observation, index))
          .filter((item) => item.source !== 'computed'),
      },
    ],
    focusedAssessments: response.focusedAssessments,
    questionStage: response.questionStage,
    isNoFocusedLogic,
    isNotClinicalConcept,
    isComprehensive,
  };
}

export async function submitClinicalNote(
  encounterId: string,
  note: string,
  options: SubmitClinicalNoteOptions,
): Promise<TextModeSubmitResponse> {
  const {
    engagement,
    observations,
    patient,
    processNote,
    extractions,
    assessmentScope,
  } = options;

  const correlation = newCorrelation();

  const requestBody: FlexiEvaluateRequestBody = {
    encounterId,
    engagement,
    note,
    processNote,
    patient: patient ?? {},
  };
  if (observations) requestBody.observations = observations;
  if (extractions && extractions.length > 0) requestBody.extractions = extractions;
  if (assessmentScope) requestBody.assessmentScope = assessmentScope;

  const body = JSON.stringify(requestBody);

  let response: Response;

  try {
    response = await fetch(FLEXI_EVALUATE_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(correlation?.headers ?? {}),
      },
      credentials: 'include',
      body,
    });
  } catch (error) {
    // fetch rejects with no Response when offline or the connection fails, so the
    // status branch below never sees it.
    logger.error(
      LogCategory.NETWORK,
      'network.evaluate.unreachable',
      { encounterId, type: (error as Error)?.name, reason: (error as Error)?.message },
      'Clinical note submit did not reach the server',
      { correlationId: correlation?.correlationId },
    ).then();

    throw error;
  }

  if (!response.ok) {
    if (response.status === 503) {
      const errorBody = await response.json().catch(() => null);
      if (errorBody?.code === 'ASSESSMENT_CONFIGURATION_ERROR') {
        throw new AssessmentConfigurationError('Assessment configuration is unavailable');
      }
    }
    logger.error(
      LogCategory.NETWORK,
      'network.evaluate.failed',
      { status: response.status, encounterId },
      'Failed to submit clinical note',
      { correlationId: correlation?.correlationId },
    ).then();

    throw new Error(`Failed to submit clinical note (${response.status})`);
  }

  const payload = await response.json() as FlexiEvaluateResponse;


  return mapEvaluateResponseToTextMode(payload);
}
